import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

type AppointmentType =
  | ""
  | "Facilitation meeting"
  | "Genetic test"
  | "Establishment hearing"
  | "Modification hearing";

export interface AppointmentData {
  id?: string;
  date: Dayjs | null;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  appointmentType: AppointmentType;
  caseId?: string;
  facilitator?: string;
  putativeParent?: string;
  participants?: string[];
  meetingMethod?: string;
  labName?: string;
  hearingType?: string;
  judge?: string;
  courtAddress?: string;
  courtCity?: string;
  courtState?: string;
  courtRoom?: string;
  notes?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: AppointmentData) => void;
  initialData?: AppointmentData | null;
  mode?: "add" | "edit";
}

const AddAppointmentDialog: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  initialData,
  mode = "add",
}) => {
  const { control, handleSubmit, reset, watch } = useForm<AppointmentData>({
    defaultValues: initialData || {
      date: dayjs(),
      startTime: dayjs(),
      endTime: dayjs(),
      appointmentType: "",
    },
  });

  useEffect(() => {
    reset(initialData || {
      date: dayjs(),
      startTime: dayjs(),
      endTime: dayjs(),
      appointmentType: "",
    });
  }, [initialData, open, reset]);

  const appointmentType = watch("appointmentType");

  const renderDynamicFields = () => {
    switch (appointmentType) {
      case "Facilitation meeting":
        return (
          <>
            <Controller
              name="caseId"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Case ID" fullWidth margin="normal" />
              )}
            />
            <Controller
              name="facilitator"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Facilitator" fullWidth margin="normal" />
              )}
            />
            <Controller
              name="putativeParent"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Putative parent’s name"
                  fullWidth
                  margin="normal"
                />
              )}
            />
            <Controller
              name="participants"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Other participants"
                  fullWidth
                  margin="normal"
                  value={field.value?.join(", ") || ""}
                  onChange={(e) => field.onChange(e.target.value.split(","))}
                />
              )}
            />
            <Controller
              name="meetingMethod"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel id="meeting-method-label">Meeting method</InputLabel>
                  <Select {...field} labelId="meeting-method-label">
                    <MenuItem value="In person">In person</MenuItem>
                    <MenuItem value="Phone call">Phone call</MenuItem>
                    <MenuItem value="Video call">Video call</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </>
        );

      case "Genetic test":
        return (
          <>
            <Controller
              name="caseId"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Case ID" fullWidth margin="normal" />
              )}
            />
            <Controller
              name="participants"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Participants"
                  placeholder="Separate names with commas"
                  fullWidth
                  margin="normal"
                  value={field.value?.join(", ") || ""}
                  onChange={(e) => field.onChange(e.target.value.split(","))}
                />
              )}
            />
            <Controller
              name="labName"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Lab to test at" fullWidth margin="normal" />
              )}
            />
          </>
        );

      case "Establishment hearing":
      case "Modification hearing":
        return (
          <>
            <Controller
              name="caseId"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Case ID" fullWidth margin="normal" />
              )}
            />
            <Controller
              name="participants"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Participants"
                  fullWidth
                  margin="normal"
                  value={field.value?.join(", ") || ""}
                  onChange={(e) => field.onChange(e.target.value.split(","))}
                />
              )}
            />
            <Controller
              name="hearingType"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Hearing type" fullWidth margin="normal" />
              )}
            />
            <Controller
              name="judge"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Judge" fullWidth margin="normal" />
              )}
            />
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Court Address
            </Typography>
            <Stack spacing={1} direction="column" mt={1}>
              <Controller
                name="courtAddress"
                control={control}
                render={({ field }) => <TextField {...field} label="Address" />}
              />
              <Controller
                name="courtCity"
                control={control}
                render={({ field }) => <TextField {...field} label="City" />}
              />
              <Controller
                name="courtState"
                control={control}
                render={({ field }) => <TextField {...field} label="State" />}
              />
              <Controller
                name="courtRoom"
                control={control}
                render={({ field }) => <TextField {...field} label="Room / Court No." />}
              />
            </Stack>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === "edit" ? "Update Appointment" : "Add Appointment"}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  label="Date & Time"
                  onChange={(date) => field.onChange(date)}
                  slots={{ textField: TextField }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
            <Controller
              name="startTime"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  label="Start Time"
                  onChange={(date) => field.onChange(date)}
                  slots={{ textField: TextField }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
            <Controller
              name="endTime"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  label="End Time"
                  onChange={(date) => field.onChange(date)}
                  slots={{ textField: TextField }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
          </Stack>

          <Controller
            name="appointmentType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="appt-type-label">Appointment type</InputLabel>
                <Select {...field} labelId="appt-type-label">
                  <MenuItem value="Establishment hearing">Establishment hearing</MenuItem>
                  <MenuItem value="Facilitation meeting">Facilitation meeting</MenuItem>
                  <MenuItem value="Genetic test">Genetic test</MenuItem>
                  <MenuItem value="Modification hearing">Modification hearing</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {renderDynamicFields()}

          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Notes" fullWidth margin="normal" multiline rows={3} />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSave)}>
          {mode === "edit" ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAppointmentDialog;
